import { useEffect, useMemo, useRef, useState } from "react";
import { getPublicApiOrigin } from "../config/env";

/**
 * Interview recording with its proctoring flags (dots on the timeline, a badge
 * over the video while a flag is active, and a clickable list below).
 *
 * Data: reportAPI.sessionRecording → { parts, events } (backend
 * app/services/reports/recording_view.py). `parts` are one recording per sitting
 * (a resumed exam has several), each with a signed URL the <video> streams
 * directly; `events[].offset` is seconds from the start of `events[].part`.
 */

const SEVERITY_COLOR = {
  Critical: "#B91C1C",
  High:     "#EF4444",
  Medium:   "#F59E0B",
  Info:     "#94A3B8",
};

const colorOf = (severity) => SEVERITY_COLOR[severity] || "#6366F1";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export default function AnnotatedVideoPlayer({ parts = [], events = [] }) {
  const videoRef = useRef(null);
  /** MediaRecorder WebM can report duration Infinity until we seek to the end once */
  const webmDurationProbeRef = useRef(false);
  /** Seek target to apply once a newly selected part has loaded */
  const pendingSeekRef = useRef(null);
  const [partNo, setPartNo] = useState(parts[0]?.part ?? 1);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);

  const part = parts.find((p) => p.part === partNo) || parts[0];
  const src = part ? `${getPublicApiOrigin()}${part.url}` : null;

  const partEvents = useMemo(
    () => events.filter((e) => e.part === part?.part).sort((a, b) => a.offset - b.offset),
    [events, part]
  );
  const active = partEvents.filter((e) => Math.abs(e.offset - current) < 2);

  useEffect(() => {
    setStatus("loading");
    setCurrent(0);
    setDuration(part?.duration_seconds || 0);
    setPlaying(false);
    webmDurationProbeRef.current = false;
  }, [src, part]);

  const play = () => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };

  const seekTo = (offset, partNumber = part?.part) => {
    if (offset == null || !isFinite(offset)) return;
    if (partNumber !== part?.part) {
      pendingSeekRef.current = offset;
      setPartNo(partNumber);
      return;
    }
    if (videoRef.current) {
      videoRef.current.currentTime = offset;
      play();
    }
  };

  const seekBar = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = ((e.clientX - rect.left) / rect.width) * duration;
    if (videoRef.current && isFinite(t)) videoRef.current.currentTime = t;
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) v.pause();
    else play();
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.duration === Infinity) {
      webmDurationProbeRef.current = true;
      video.currentTime = 1e101;
      return;
    }
    if (isFinite(video.duration)) setDuration(video.duration);
    setStatus("ready");
    if (pendingSeekRef.current != null) {
      video.currentTime = pendingSeekRef.current;
      pendingSeekRef.current = null;
      play();
    }
  };

  const handleSeeked = () => {
    const video = videoRef.current;
    if (!video || !webmDurationProbeRef.current || !isFinite(video.duration)) return;
    webmDurationProbeRef.current = false;
    setDuration(video.duration);
    video.currentTime = pendingSeekRef.current ?? 0;
    pendingSeekRef.current = null;
    setStatus("ready");
  };

  const pct = duration ? (current / duration) * 100 : 0;

  if (!part) return null;

  return (
    <div style={{ background: "#0F172A", borderRadius: 12, overflow: "hidden" }}>
      {/* Parts of a resumed exam */}
      {parts.length > 1 && (
        <div style={{ display: "flex", gap: 6, padding: "10px 14px 0", flexWrap: "wrap" }}>
          {parts.map((p) => {
            const on = p.part === part.part;
            const flags = events.filter((e) => e.part === p.part).length;
            return (
              <button
                key={p.part}
                type="button"
                onClick={() => setPartNo(p.part)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                  border: "1px solid " + (on ? "#6366F1" : "#334155"),
                  background: on ? "#6366F1" : "transparent", color: on ? "white" : "#CBD5E1",
                }}
              >
                Part {p.part}{p.duration_seconds ? ` · ${fmt(p.duration_seconds)}` : ""}{flags ? ` · ${flags} flag${flags === 1 ? "" : "s"}` : ""}
              </button>
            );
          })}
        </div>
      )}

      {/* Video */}
      <div style={{ position: "relative", background: "#000", marginTop: parts.length > 1 ? 10 : 0 }}>
        <video
          key={src}
          ref={videoRef}
          src={src}
          preload="metadata"
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
          onSeeked={handleSeeked}
          onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={() => setStatus("error")}
          style={{ width: "100%", display: "block", maxHeight: "52vh", background: "#000", visibility: status === "ready" ? "visible" : "hidden" }}
        />
        {status !== "ready" && (
          <div style={{
            position: "absolute", inset: 0, minHeight: 240,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: status === "error" ? "#FCA5A5" : "#94A3B8", fontSize: 13, textAlign: "center", padding: 16,
          }}>
            {status === "error" ? "The recording could not be loaded. Try again later." : "Loading recording…"}
          </div>
        )}

        {/* Active flag overlay */}
        {status === "ready" && active.map((v, i) => (
          <div key={`${v.offset}-${i}`} style={{
            position: "absolute", top: 12 + i * 40, left: 12,
            background: colorOf(v.severity),
            color: "white", fontSize: 12, fontWeight: 600,
            padding: "5px 12px", borderRadius: 6,
            display: "flex", alignItems: "center", gap: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,.4)",
            animation: "fadeIn .2s ease",
          }}>
            <span>⚠</span>
            {v.label}
          </div>
        ))}
      </div>

      {/* Timeline bar */}
      <div style={{ padding: "10px 14px 0" }}>
        <div
          onClick={seekBar}
          style={{ position: "relative", height: 6, background: "#334155", borderRadius: 3, cursor: "pointer" }}
        >
          <div style={{
            position: "absolute", left: 0, top: 0, height: "100%",
            width: pct + "%", background: "#6366F1", borderRadius: 3,
            transition: "width .1s linear",
          }}/>
          {duration > 0 && partEvents.map((v, i) => {
            const isActive = Math.abs(v.offset - current) < 2;
            const dotColor = colorOf(v.severity);
            return (
              <div
                key={i}
                title={`${v.label} at ${fmt(v.offset)}`}
                onClick={(e) => { e.stopPropagation(); seekTo(v.offset); }}
                style={{
                  position: "absolute",
                  left: `${Math.min(100, (v.offset / duration) * 100)}%`,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: isActive ? 14 : 8,
                  height: isActive ? 14 : 8,
                  borderRadius: "50%",
                  background: dotColor,
                  border: isActive ? "2px solid white" : "1.5px solid #0F172A",
                  cursor: "pointer",
                  zIndex: 2,
                  transition: "all .15s ease",
                  boxShadow: isActive ? `0 0 6px ${dotColor}` : "none",
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px 12px", color: "white" }}>
        <button
          type="button"
          onClick={togglePlay}
          disabled={status !== "ready"}
          aria-label={playing ? "Pause" : "Play"}
          style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "#6366F1", border: "none", cursor: status === "ready" ? "pointer" : "default",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontSize: 14, flexShrink: 0, opacity: status === "ready" ? 1 : 0.5,
          }}
        >
          {playing ? "⏸" : "▶"}
        </button>
        <span style={{ fontSize: 12, color: "#94A3B8", fontVariantNumeric: "tabular-nums" }}>
          {fmt(current)} / {fmt(duration)}
        </span>
        <span style={{ fontSize: 11, color: "#64748B", marginLeft: "auto" }}>
          {events.length
            ? `${events.length} proctoring flag${events.length !== 1 ? "s" : ""}${parts.length > 1 ? ` · ${partEvents.length} in this part` : ""}`
            : "No proctoring flags"}
        </span>
      </div>

      {/* Flag index below player */}
      {partEvents.length > 0 && (
        <div style={{ borderTop: "1px solid #1E293B", maxHeight: 200, overflowY: "auto" }}>
          {partEvents.map((v, i) => (
            <div
              key={i}
              onClick={() => seekTo(v.offset)}
              title={v.description}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 14px", cursor: "pointer",
                borderBottom: "1px solid #1E293B",
                transition: "background .12s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#1E293B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: colorOf(v.severity) }}/>
              <span style={{ fontSize: 12, color: "#CBD5E1", flex: 1 }}>{v.label}</span>
              <span style={{ fontSize: 11, color: "#818CF8", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {fmt(v.offset)}
              </span>
              <span style={{
                fontSize: 10, padding: "1px 6px", borderRadius: 4,
                background: colorOf(v.severity) + "22",
                color: colorOf(v.severity),
                fontWeight: 600, textTransform: "uppercase",
              }}>
                {v.severity}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </div>
  );
}
